// The Month filters on the Bulk Bill Run board and the Bulk Customer Report.
//
// A bill counts for every month its period touches, not only the month it
// starts in: retailers bill 31 Jul – 31 Aug, and that bill has to be found
// under August as well as July. Bill dates are `YYYY-MM-DD` strings, compared
// as strings so no time zone can move a date into the neighbouring month.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type Period = { bill_from?: string | null; bill_to?: string | null };

export type MonthOption = { key: string; label: string };

/** `2026-08` → `Aug 2026`. */
export function monthLabel(key: string): string {
  const [year, month] = key.split('-');
  return `${MONTHS[Number(month) - 1]} ${year}`;
}

/** True when the bill's period touches the month `key` (`YYYY-MM`). */
export function billInMonth(bill: Period, key: string): boolean {
  if (!bill.bill_from || !bill.bill_to) return false;
  return bill.bill_from.slice(0, 7) <= key && bill.bill_to.slice(0, 7) >= key;
}

/**
 * True when the bill's period touches any month from `fromKey` to `toKey`
 * (`YYYY-MM`, inclusive). Either end may be null for an open range; the two
 * are swapped if given the wrong way round.
 */
export function billInMonthRange(bill: Period, fromKey: string | null, toKey: string | null): boolean {
  if (!fromKey && !toKey) return true;
  if (!bill.bill_from || !bill.bill_to) return false;
  let lo = fromKey ?? '0000-00';
  let hi = toKey ?? '9999-99';
  if (lo > hi) [lo, hi] = [hi, lo];
  return bill.bill_from.slice(0, 7) <= hi && bill.bill_to.slice(0, 7) >= lo;
}

/** Every month from the period's first to its last, as `YYYY-MM`. */
function monthsOf(bill: Period): string[] {
  if (!bill.bill_from || !bill.bill_to) return [];
  let year = Number(bill.bill_from.slice(0, 4));
  let month = Number(bill.bill_from.slice(5, 7));
  const last = bill.bill_to.slice(0, 7);
  const keys: string[] = [];
  let key = `${year}-${String(month).padStart(2, '0')}`;
  while (key <= last) {
    keys.push(key);
    month += 1;
    if (month > 12) { month = 1; year += 1; }
    key = `${year}-${String(month).padStart(2, '0')}`;
  }
  return keys;
}

/** The months the dropdown offers: each month any bill touches, newest first. */
export function billMonthOptions(bills: Period[]): MonthOption[] {
  const keys = new Set<string>();
  for (const bill of bills) for (const key of monthsOf(bill)) keys.add(key);
  return [...keys].sort().reverse().map((key) => ({ key, label: monthLabel(key) }));
}

/** `YYYY-MM` shifted by `n` months (negative goes back). */
export function addMonths(key: string, n: number): string {
  const total = Number(key.slice(0, 4)) * 12 + (Number(key.slice(5, 7)) - 1) + n;
  const year = Math.floor(total / 12);
  return `${year}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/** Every month from `from` to `to`, inclusive and ascending; swapped if reversed. */
export function monthsInRange(from: string, to: string): string[] {
  const [lo, hi] = from <= to ? [from, to] : [to, from];
  const keys: string[] = [];
  for (let key = lo; key <= hi; key = addMonths(key, 1)) keys.push(key);
  return keys;
}

/** The last day of the month `key`, as `YYYY-MM-DD`. */
export function monthEnd(key: string): string {
  const day = new Date(Date.UTC(Number(key.slice(0, 4)), Number(key.slice(5, 7)), 0)).getUTCDate();
  return `${key}-${String(day).padStart(2, '0')}`;
}

/** The most recent month that has fully ended, as `YYYY-MM`, in `timeZone`. */
export function lastCompletedMonth(now: Date = new Date(), timeZone = 'Australia/Sydney'): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit' }).formatToParts(now);
  const y = parts.find((p) => p.type === 'year')!.value;
  const m = parts.find((p) => p.type === 'month')!.value;
  return addMonths(`${y}-${m}`, -1);
}
